import type { Identity } from '@rummy/protocol';

/**
 * Credential calls ride plain HTTP; the socket stays a game transport. A
 * successful call stores the new token and the caller re-identifies the
 * socket, which rebuilds everything from the server's snapshot — the same
 * path as any reconnection.
 */

const TOKEN_KEY = 'rummy.token';

export interface AuthSuccess {
  ok: true;
  token: string;
  identity: Identity;
  /** No face/name chosen yet — show the profile step. */
  fresh: boolean;
}

export type AuthError =
  | 'email-taken'
  | 'invalid-credentials'
  | 'invalid-email'
  | 'weak-password'
  | 'rate-limited'
  | 'network';

export type AuthOutcome = AuthSuccess | { ok: false; error: AuthError };

async function post(path: string, body: Record<string, unknown>): Promise<AuthOutcome> {
  let data: { ok?: boolean; error?: string; token?: string } & Partial<AuthSuccess>;
  try {
    const res = await fetch(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ...body, token: localStorage.getItem(TOKEN_KEY) }),
    });
    data = (await res.json()) as typeof data;
  } catch {
    return { ok: false, error: 'network' };
  }
  if (data.ok && data.token && data.identity) {
    localStorage.setItem(TOKEN_KEY, data.token);
    // The next socket hello sends the locally cached profile; align it with
    // the account we just signed into so it cannot clobber the server's copy.
    localStorage.setItem(
      'rummy.profile',
      JSON.stringify({ handle: data.identity.handle, avatar: data.identity.avatar }),
    );
    return { ok: true, token: data.token, identity: data.identity, fresh: data.fresh ?? false };
  }
  const known: AuthError[] = [
    'email-taken', 'invalid-credentials', 'invalid-email', 'weak-password', 'rate-limited',
  ];
  return { ok: false, error: known.includes(data.error as AuthError) ? (data.error as AuthError) : 'network' };
}

export const authApi = {
  register: (email: string, password: string) => post('/api/auth/register', { email, password }),
  login: (email: string, password: string) => post('/api/auth/login', { email, password }),
  google: (credential: string) => post('/api/auth/google', { credential }),

  async config(): Promise<{ googleClientId: string | null }> {
    try {
      const res = await fetch('/api/auth/config');
      return (await res.json()) as { googleClientId: string | null };
    } catch {
      return { googleClientId: null };
    }
  },
};

// ------------------------------------------------------- google sign-in (GIS)

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: Record<string, unknown>) => void;
          renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
        };
      };
    };
  }
}

let gisLoading: Promise<void> | null = null;

function loadGis(): Promise<void> {
  if (window.google?.accounts) return Promise.resolve();
  gisLoading ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      gisLoading = null;
      reject(new Error('gis load failed'));
    };
    document.head.appendChild(script);
  });
  return gisLoading;
}

/**
 * Renders the official Google button into `parent`. Resolves credentials via
 * `onCredential`; failures to load (offline, blocked) reject and the caller
 * simply leaves email + guest as the doors in.
 */
export async function mountGoogleButton(
  parent: HTMLElement,
  clientId: string,
  onCredential: (credential: string) => void,
): Promise<void> {
  await loadGis();
  const google = window.google;
  if (!google) throw new Error('gis unavailable');
  google.accounts.id.initialize({
    client_id: clientId,
    callback: (response: { credential?: string }) => {
      if (response.credential) onCredential(response.credential);
    },
  });
  google.accounts.id.renderButton(parent, {
    theme: 'outline',
    size: 'large',
    shape: 'pill',
    width: parent.clientWidth || 320,
    text: 'continue_with',
  });
}
